import React, { useState, useRef } from 'react';
import { X, Link, Calendar, AlertCircle, Upload, Globe, ChevronLeft, Check, RefreshCw } from 'lucide-react';
import { COLORS } from '../../../utils/calendar/constants';
import { useFocusTrap } from '../../../hooks/useFocusTrap';
import { ColorPicker } from '../../ui/ColorPicker';

interface AddCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (name: string, url: string, color: string) => Promise<any>;
  onImportFile?: (name: string, content: string, color: string) => Promise<any>;
  googleCalendarConnected?: boolean;
  onConnectGoogle?: () => void;
}

type Step = 'choose' | 'url' | 'file' | 'google';

export default function AddCalendarModal({ isOpen, onClose, onAdd, onImportFile, googleCalendarConnected, onConnectGoogle }: AddCalendarModalProps) {
  const [step, setStep] = useState<Step>('choose');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [color, setColor] = useState(COLORS[0].bg);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileContent, setFileContent] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, isOpen);

  if (!isOpen) return null;

  const resetForm = () => {
    setStep('choose');
    setName('');
    setUrl('');
    setColor(COLORS[0].bg);
    setFileName('');
    setFileContent('');
    setError('');
    setLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const goBack = () => {
    setError('');
    setStep('choose');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.ics')) {
      setError('Please select an .ics file');
      return;
    }

    setFileName(file.name);
    setError('');

    if (!name) {
      setName(file.name.replace('.ics', ''));
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      if (!content.includes('BEGIN:VCALENDAR')) {
        setError('Invalid ICS file format');
        setFileContent('');
        return;
      }
      setFileContent(content);
    };
    reader.onerror = () => setError('Failed to read file');
    reader.readAsText(file);
  };

  const handleUrlSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) { setError('Please enter a calendar name'); return; }
    if (!url.trim()) { setError('Please enter an ICS URL'); return; }

    const lowerUrl = url.toLowerCase();
    if (!lowerUrl.includes('.ics') && !lowerUrl.includes('calendar') && !lowerUrl.includes('ical') && !lowerUrl.includes('caldav') && !lowerUrl.startsWith('webcal://') && !lowerUrl.startsWith('webcals://')) {
      setError('URL should be an ICS calendar feed');
      return;
    }

    setLoading(true);
    try {
      const result = await onAdd(name.trim(), url.trim(), color);
      if (result) { resetForm(); onClose(); }
      else { setError('Failed to add calendar. Check the URL or try file import.'); }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('iCloud') || msg.includes('publicly')) {
        setError('Could not fetch calendar. Try exporting as .ics and using file import.');
      } else {
        setError('Failed to fetch calendar. Try file import instead.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) { setError('Please enter a calendar name'); return; }
    if (!fileContent) { setError('Please select an ICS file'); return; }
    if (!onImportFile) { setError('File import not supported'); return; }

    setLoading(true);
    try {
      const result = await onImportFile(name.trim(), fileContent, color);
      if (result) { resetForm(); onClose(); }
      else { setError('Failed to import calendar file'); }
    } catch {
      setError('Failed to import calendar file');
    } finally {
      setLoading(false);
    }
  };

  const stepTitle = step === 'choose' ? 'Add Calendar'
    : step === 'url' ? 'Subscribe to URL'
    : step === 'file' ? 'Import File'
    : 'Google Calendar';

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeInBackdrop"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div ref={modalRef} role="dialog" aria-modal="true" className="bg-[var(--modal-bg)] rounded-2xl w-full max-w-sm shadow-xl animate-fadeIn">
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-[var(--border-primary)] flex items-center gap-3">
          {step !== 'choose' && (
            <button onClick={goBack} className="p-0.5 hover:bg-[var(--sidebar-hover)] rounded-md transition-colors" aria-label="Go back">
              <ChevronLeft className="w-4 h-4 text-[var(--content-secondary)]" />
            </button>
          )}
          <h2 className="flex-1 text-[15px] font-semibold text-[var(--content-primary)]">{stepTitle}</h2>
          <button onClick={handleClose} className="p-0.5 hover:bg-[var(--sidebar-hover)] rounded-md transition-colors" aria-label="Close">
            <X className="w-4 h-4 text-[var(--content-secondary)]" />
          </button>
        </div>

        {/* Step: Choose */}
        {step === 'choose' && (
          <div className="p-4 space-y-2">
            <button
              onClick={() => setStep('url')}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[var(--sidebar-hover)] transition-colors text-left"
            >
              <div className="w-9 h-9 rounded-lg bg-[var(--accent-subtle)] flex items-center justify-center flex-shrink-0">
                <Globe className="w-4.5 h-4.5 text-[var(--accent-primary)]" />
              </div>
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-[var(--content-primary)]">Subscribe to URL</p>
                <p className="text-[11px] text-[var(--content-tertiary)]">Stay synced with an ICS calendar feed</p>
              </div>
            </button>

            <button
              onClick={() => setStep('file')}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[var(--sidebar-hover)] transition-colors text-left"
            >
              <div className="w-9 h-9 rounded-lg bg-[var(--surface-tertiary)] flex items-center justify-center flex-shrink-0">
                <Upload className="w-4.5 h-4.5 text-[var(--content-secondary)]" />
              </div>
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-[var(--content-primary)]">Import File</p>
                <p className="text-[11px] text-[var(--content-tertiary)]">One-time import from an .ics file</p>
              </div>
            </button>

            <button
              onClick={() => {
                if (!googleCalendarConnected && onConnectGoogle) {
                  setStep('google');
                } else if (googleCalendarConnected) {
                  setStep('google');
                }
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[var(--sidebar-hover)] transition-colors text-left"
            >
              <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-4.5 h-4.5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-[var(--content-primary)]">Google Calendar</p>
                <p className="text-[11px] text-[var(--content-tertiary)]">Two-way sync with your Google account</p>
              </div>
              {googleCalendarConnected && (
                <span className="text-[10px] font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-500/10 px-2 py-0.5 rounded-full">Connected</span>
              )}
            </button>
          </div>
        )}

        {/* Step: URL */}
        {step === 'url' && (
          <form onSubmit={handleUrlSubmit} className="p-4 space-y-3">
            {error && (
              <div className="flex items-start gap-2 p-2.5 bg-[var(--status-error-bg)] text-[var(--error)] text-xs rounded-lg">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-[var(--content-secondary)] mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Work Calendar"
                className="w-full px-3 py-2 border border-[var(--input-border)] bg-[var(--input-bg)] text-[var(--content-primary)] placeholder-[var(--content-tertiary)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] text-sm"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-[var(--content-secondary)] mb-1">ICS Feed URL</label>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="webcal:// or https://..."
                className="w-full px-3 py-2 border border-[var(--input-border)] bg-[var(--input-bg)] text-[var(--content-primary)] placeholder-[var(--content-tertiary)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] text-sm"
              />
              <p className="mt-1 text-[10px] text-[var(--content-tertiary)]">
                Supports webcal://, iCloud, Google Calendar, Outlook and other ICS feeds
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-[var(--content-secondary)] mb-1.5">Color</label>
              <ColorPicker
                value={color}
                onChange={setColor}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-1 px-4 py-2 bg-[var(--accent-primary)] text-white rounded-lg hover:opacity-90 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {loading ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>
        )}

        {/* Step: File */}
        {step === 'file' && (
          <form onSubmit={handleFileSubmit} className="p-4 space-y-3">
            {error && (
              <div className="flex items-start gap-2 p-2.5 bg-[var(--status-error-bg)] text-[var(--error)] text-xs rounded-lg">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-[var(--content-secondary)] mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., iCloud Personal"
                className="w-full px-3 py-2 border border-[var(--input-border)] bg-[var(--input-bg)] text-[var(--content-primary)] placeholder-[var(--content-tertiary)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] text-sm"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-[var(--content-secondary)] mb-1">ICS File</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".ics"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full px-4 py-3 border-2 border-dashed border-[var(--border-primary)] rounded-lg hover:border-[var(--accent-primary)] hover:bg-[var(--accent-subtle)] transition-colors text-sm"
              >
                {fileName ? (
                  <span className="text-[var(--accent-primary)] font-medium">{fileName}</span>
                ) : (
                  <span className="text-[var(--content-tertiary)]">Click to select .ics file</span>
                )}
              </button>
              <p className="mt-1 text-[10px] text-[var(--content-tertiary)]">
                Export from: iCloud (Share &rarr; Export), Outlook (File &rarr; Save Calendar)
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-[var(--content-secondary)] mb-1.5">Color</label>
              <ColorPicker
                value={color}
                onChange={setColor}
              />
            </div>

            <button
              type="submit"
              disabled={loading || !fileContent}
              className="w-full mt-1 px-4 py-2 bg-[var(--accent-primary)] text-white rounded-lg hover:opacity-90 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {loading ? 'Importing...' : 'Import'}
            </button>
          </form>
        )}

        {/* Step: Google Calendar */}
        {step === 'google' && (
          <div className="p-4">
            {googleCalendarConnected ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-500/10 rounded-lg">
                  <Check className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <div>
                    <p className="text-[13px] font-medium text-[var(--content-primary)]">Google Calendar connected</p>
                    <p className="text-[11px] text-[var(--content-tertiary)]">Your Google Calendar is syncing with Mozayc</p>
                  </div>
                </div>
                <p className="text-[11px] text-[var(--content-tertiary)]">
                  Manage sync settings in Settings &rarr; Calendar &rarr; Google Calendar.
                </p>
                <button
                  onClick={handleClose}
                  className="w-full px-4 py-2 border border-[var(--border-primary)] text-[var(--content-primary)] rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors text-sm font-medium"
                >
                  Done
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-[var(--content-secondary)]">
                  Connect your Google account to sync events two-way between Mozayc and Google Calendar.
                </p>
                <button
                  onClick={() => {
                    onConnectGoogle?.();
                    handleClose();
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[var(--accent-primary)] text-white rounded-lg hover:opacity-90 transition-colors text-sm font-medium"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Connect Google Calendar
                </button>
                <p className="text-[10px] text-[var(--content-tertiary)] text-center">
                  You can manage this later in Settings
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
